import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import { openRazorpayOrderCheckout } from '../subscriptionAdmin/razorpay.js';
import {
  confirmContractCheckout,
  fetchContractCheckout,
  fetchSubscriptionStatus,
  initiateContractCheckout,
} from '../services/userSubscriptionApi.js';
import { USER_APP_ROBOTS_PATH } from './paths.js';
import {
  PAYMENT_FLOW_SUBSCRIPTION_ACTIVE,
  isContractCheckoutPaymentContext,
  resolvePathFromPaymentContext,
} from '../utils/clientAppRedirect.js';
import theme from '../UserDashboard/theme.js';
import { getClientEmail } from '../utils/getClientEmail';

const accent = theme.colors.brandDark;

function formatMoney(amount, currency = 'INR') {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${currency || 'INR'} ${amount}`;
  }
}

function getEmail() {
  return getClientEmail();
}

/**
 * Pay admin-approved contract — GET/POST /user/subscription/contract-checkout/*
 * @param {{ compact?: boolean, onPaymentSuccess?: () => void }} props
 */
export default function ContractCheckoutView({ compact = false, onPaymentSuccess }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [checkout, setCheckout] = useState(null);
  const [paymentContext, setPaymentContext] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const loadCheckout = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const statusRes = await fetchSubscriptionStatus();
      const ctx = statusRes.data?.paymentContext ?? null;
      setPaymentContext(ctx);

      if (ctx?.paymentFlow === PAYMENT_FLOW_SUBSCRIPTION_ACTIVE || ctx?.canAccessRobots) {
        navigate(USER_APP_ROBOTS_PATH, { replace: true });
        return;
      }
      if (!isContractCheckoutPaymentContext(ctx)) {
        navigate(resolvePathFromPaymentContext(ctx), { replace: true });
        return;
      }

      const res = await fetchContractCheckout();
      setCheckout(res.data?.data ?? null);
    } catch (err) {
      setCheckout(null);
      setError(
        err.response?.data?.message ||
          err.message ||
          'Could not load contract checkout.'
      );
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadCheckout();
  }, [loadCheckout]);

  const startContractPayment = async () => {
    const contract = checkout?.contract;
    if (!contract?._id || paying) return;
    const email = getEmail();
    if (!email) {
      setSnackbar({ open: true, message: 'Please sign in again.', severity: 'error' });
      return;
    }

    setPaying(true);
    try {
      const initiateRes = await initiateContractCheckout({ contractId: contract._id });
      const payload = initiateRes.data?.data || initiateRes.data;

      if (payload?.mode === 'free_month') {
        setSnackbar({
          open: true,
          message:
            payload.message ||
            'Free month applied — your subscription is activating.',
          severity: 'success',
        });
        onPaymentSuccess?.();
        return;
      }

      if (payload?.mode === 'already_paid') {
        setSnackbar({
          open: true,
          message: payload.message || 'Payment already completed for this billing cycle.',
          severity: 'success',
        });
        onPaymentSuccess?.();
        return;
      }

      if (payload?.mode !== 'razorpay_order' && payload?.mode !== 'mandate_setup') {
        throw new Error(initiateRes.data?.message || 'Payment could not be started.');
      }

      const { razorpayKeyId, order, customerId, charge, checkoutOptions } = payload;
      if (!razorpayKeyId || !order?.id) {
        throw new Error('Missing Razorpay order details.');
      }

      const confirmRes = await openRazorpayOrderCheckout({
        razorpayKeyId,
        order,
        customerId,
        charge,
        checkoutOptions,
        description:
          charge?.checkoutDescription ||
          charge?.billingSummary?.checkoutDescription ||
          `Contract ${contract.contractNumber || ''}`.trim(),
        prefill: { email },
        onConfirm: (razorpayPayload) => confirmContractCheckout(razorpayPayload),
      });

      const refundLabel = confirmRes?.data?.data?.refund?.label;
      const successMessage =
        confirmRes?.data?.message ||
        (refundLabel
          ? `${refundLabel}. Your contract is active — you can open Robots.`
          : 'Payment confirmed. Your contract is now active — you can open Robots.');

      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success',
      });
      onPaymentSuccess?.();
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || 'Contract payment failed.';
      if (msg !== 'Payment cancelled') {
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }

  const contract = checkout?.contract;
  const billingSummary =
    checkout?.billingSummary ||
    checkout?.charge?.billingSummary ||
    paymentContext?.billingSummary ||
    paymentContext?.openContract?.billingSummary ||
    null;
  const currency =
    billingSummary?.currency || contract?.billing?.currency || 'INR';
  const contextMessage = paymentContext?.message || '';
  if (!contract) {
    const open = paymentContext?.openContract;
    if (open?.contractNumber) {
      return (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Contract {open.contractNumber} is awaiting payment, but checkout details could not be
          loaded. {contextMessage}
          <Box sx={{ mt: 1.5 }}>
            <Button variant="outlined" size="small" onClick={loadCheckout}>
              Retry
            </Button>
          </Box>
        </Alert>
      );
    }
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        No contract is awaiting payment. If you have not chosen a plan yet, go back and select
        one from the catalog.
      </Alert>
    );
  }

  const amountDue = billingSummary?.totalAmount ?? contract.billing?.amount;

  return (
    <>
      <Box sx={compact ? undefined : { maxWidth: 720, mx: 'auto', py: { xs: 2, md: 3 } }}>
        {!compact && (
          <>
            <Typography sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 700, mb: 0.5 }}>
              Contract payment
            </Typography>
            <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary, mb: 3 }}>
              Your Spotless AI contract is ready. Pay below to activate your subscription and UV hour
              quota.
            </Typography>
          </>
        )}
        {contextMessage ? (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            {contextMessage}
          </Alert>
        ) : null}
        {paymentContext?.mandateRefundSummary?.status === 'completed' ? (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {paymentContext.mandateRefundSummary.message ||
              paymentContext.mandateRefundSummary.label}
          </Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: `${theme.radius.lg}px`,
            border: `1px solid ${theme.colors.borderDefault}`,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: theme.colors.textSecondary, mb: 1 }}>
            CONTRACT
          </Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: accent }}>
            {contract.contractNumber || '—'}
          </Typography>
          <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary, mt: 0.5 }}>
            Status: {String(contract.status || '—').replace(/_/g, ' ')}
          </Typography>

          <Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ minWidth: 220 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary }}>
                AMOUNT DUE
              </Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: accent }}>
                {formatMoney(amountDue, currency)}
              </Typography>
              {billingSummary ? (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: `${theme.radius.md}px`,
                    bgcolor: 'rgba(37,99,235,0.06)',
                    border: `1px solid rgba(37,99,235,0.15)`,
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: accent, mb: 0.75 }}>
                    Billing summary
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary }}>
                    Amount: {formatMoney(billingSummary.amount, currency)}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary }}>
                    GST ({billingSummary.gstRate}%): {formatMoney(billingSummary.gstAmount, currency)}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: accent, mt: 0.5 }}>
                    Total: {formatMoney(billingSummary.totalAmount, currency)}
                  </Typography>
                  {billingSummary.formula ? (
                    <Typography sx={{ fontSize: 11, color: theme.colors.textSecondary, mt: 0.75 }}>
                      {billingSummary.formula}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </Box>
            {contract.commitmentMonths != null ? (
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary }}>
                  TERM
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {contract.commitmentMonths} months
                </Typography>
              </Box>
            ) : null}
            {contract.billingFlow ? (
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary }}>
                  BILLING
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {String(contract.billingFlow).replace(/_/g, ' ')}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={paying}
              onClick={startContractPayment}
              startIcon={
                paying ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <PaymentOutlinedIcon />
                )
              }
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: accent,
                '&:hover': { bgcolor: theme.colors.brandPrimary },
              }}
            >
              {paying ? 'Opening checkout…' : 'Pay & activate'}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2, width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
